import React, { useEffect, useRef, useState } from "react"

import { currentUser } from "lib/currentUser"
import Flash from "lib/flash"
import i18n from "lib/i18n"
import { Lightbox } from "components/common/Lightbox"
import { primaryButton, secondaryButton } from "components/Profile/ContactAuthor/buttonClasses"
import Field from "components/Profile/ContactAuthor/Form/Field"
import submitAuthorCommunication, {
  type FieldErrors,
} from "components/Profile/ContactAuthor/Form/submit"

export interface ContactReason {
  slug: string
  label: string
}

interface Sender {
  full_name?: string
  email?: string
}

interface ContactFormElements extends HTMLFormControlsCollection {
  reason: HTMLSelectElement
  message: HTMLTextAreaElement
}

// The legacy formtastic text control; "tailwind" opts out of the global input styles in core/forms.scss.
const textControl =
  "tailwind m-0 box-border w-full appearance-none rounded-xs border border-gray-200 bg-white p-1.5 text-sm leading-4.5 hover:border-indigo-600 focus:border-indigo-600 focus:outline-none disabled:bg-gray-200 aria-invalid:border-red-700 max-md:text-base"

interface ContactAuthorFormProps {
  authorId: number | string
  availableFor: ContactReason[]
  onClose: () => void
}

const ContactAuthorForm = ({ authorId, availableFor, onClose }: ContactAuthorFormProps) => {
  "use memo"

  const [sender, setSender] = useState<Sender | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const formRef = useRef<HTMLFormElement>(null)

  const close = () => dialogRef.current?.close()

  useEffect(() => {
    let mounted = true

    // .then/.catch, not try/catch: the React Compiler skips components with conditionals inside a try.
    currentUser()
      .then((user: Sender | null) => {
        // null without a signed-in user
        if (mounted) setSender(user || {})
      })
      .catch(() => {
        if (mounted) {
          setSender({})
          Flash.now("alert", i18n.t("author_communication.messages.error"))
        }
      })

    return () => {
      mounted = false
    }
  }, [])

  // Focus the first invalid field that can take focus; name and email are disabled.
  useEffect(() => {
    formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]:not(:disabled)')?.focus()
  }, [fieldErrors])

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault()
    if (isSubmitting || !sender) return

    setIsSubmitting(true)

    const elements = formRef.current.elements as ContactFormElements
    const result = await submitAuthorCommunication({
      authorId,
      reason: elements.reason.value,
      message: elements.message.value,
    })

    if (result.ok) {
      // Closed first, so the flash goes in <body> rather than the closing dialog.
      close()
      Flash.now("notice", i18n.t("author_communication.messages.success"))
      return
    }

    // Failures without field errors keep the previous ones.
    if (result.errors) setFieldErrors(result.errors)
    Flash.now("alert", i18n.t("author_communication.messages.error"))
    setIsSubmitting(false)
  }

  return (
    <Lightbox
      className="contact-author"
      labelledBy="contact-author-title"
      onClose={onClose}
      dialogRef={dialogRef}
    >
      <div className="p-5 max-md:p-3">
        <h1
          id="contact-author-title"
          tabIndex={-1}
          className="mb-9 border-b border-b-gray-200 pb-2.25 text-3xl max-md:text-xl max-md:leading-6.5"
        >
          {i18n.t("author_communication.label.contact_author")}
        </h1>

        <p className="ml-3">{i18n.t("author_communication.blurb.intro")}</p>

        {sender ? (
          <form ref={formRef} className="max-md:pb-10" onSubmit={handleSubmit}>
            <fieldset>
              <ol className="m-3 list-none max-md:m-0">
                <Field
                  name="from_name"
                  label={i18n.t("author_communication.label.your_name")}
                  error={fieldErrors.from_name}
                >
                  {(controlProps) => (
                    <input
                      id="from_name"
                      name="from_name"
                      type="text"
                      autoComplete="off"
                      placeholder={i18n.t("author_communication.placeholders.your_full_name")}
                      maxLength={255}
                      disabled
                      value={sender.full_name || ""}
                      className={textControl}
                      {...controlProps}
                    />
                  )}
                </Field>

                <Field
                  name="from_email"
                  label={i18n.t("author_communication.label.your_email")}
                  error={fieldErrors.from_email}
                >
                  {(controlProps) => (
                    <input
                      id="from_email"
                      name="from_email"
                      type="email"
                      autoComplete="off"
                      disabled
                      placeholder={i18n.t("author_communication.placeholders.your_email_address")}
                      value={sender.email || ""}
                      className={textControl}
                      {...controlProps}
                    />
                  )}
                </Field>

                <Field
                  name="reason"
                  label={i18n.t("author_communication.label.reason_for_contacting")}
                  error={fieldErrors.reason}
                >
                  {(controlProps) => (
                    <select
                      id="reason"
                      name="reason"
                      className="aria-invalid:border-red-700 max-md:w-full"
                      {...controlProps}
                    >
                      {availableFor.map((reason) => (
                        <option key={reason.slug} value={reason.slug}>
                          {reason.label}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>

                <Field
                  name="message"
                  label={i18n.t("author_communication.label.your_message")}
                  error={fieldErrors.message}
                >
                  {(controlProps) => (
                    <textarea
                      id="message"
                      name="message"
                      className={`${textControl} h-25`}
                      aria-required="true"
                      placeholder={i18n.t("author_communication.placeholders.your_message")}
                      {...controlProps}
                    />
                  )}
                </Field>
              </ol>
            </fieldset>
          </form>
        ) : (
          <p role="status" aria-live="polite">
            {i18n.t("common.load_more.loading")}
          </p>
        )}
      </div>

      <footer className="overflow-auto bg-gray-50 px-5 py-3.75 max-md:p-3">
        <button type="button" className={`${secondaryButton} float-left mr-2.5`} onClick={close}>
          {i18n.t("author_communication.button.close")}
        </button>

        <button
          type="button"
          data-testid="contact-submit"
          className={`${primaryButton} float-right ml-5`}
          // aria-disabled rather than disabled, so it keeps focus; handleSubmit guards repeats.
          aria-disabled={isSubmitting || !sender}
          onClick={handleSubmit}
        >
          {i18n.t("author_communication.button.contact")}
        </button>
      </footer>
    </Lightbox>
  )
}

export default ContactAuthorForm
